import { useEffect, useState } from "react";
import { api, ApiError } from "../../api/client.js";
import type { HealthResult, MonitorItem } from "../../api/types.js";

function CheckRow({ name, value }: { readonly name: string; readonly value: unknown }) {
  const record = value as { readonly ok?: boolean | null; readonly reason?: string; readonly error?: string } | boolean | null;
  let label = "Unknown";
  let className = "status-ready_for_review";
  if (typeof record === "boolean") {
    label = record ? "Healthy" : "Unhealthy";
    className = record ? "status-finalized" : "status-failed";
  } else if (record !== null && typeof record === "object") {
    if (record.ok === true) {
      label = "Healthy";
      className = "status-finalized";
    } else if (record.ok === false) {
      label = record.error ?? "Unhealthy";
      className = "status-failed";
    } else {
      label = record.reason ?? "Not configured";
      className = "status-ready_for_review";
    }
  } else if (record === null) {
    label = "Not configured";
  }
  return (
    <tr>
      <td>{name}</td>
      <td>
        <span className={`badge ${className}`}>{label}</span>
      </td>
    </tr>
  );
}

export function MonitorTab({ tenantId }: { readonly tenantId: string }) {
  const [health, setHealth] = useState<HealthResult | null>(null);
  const [items, setItems] = useState<MonitorItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    function load(): void {
      api
        .get<HealthResult>(`/videos/${tenantId}/health`)
        .then((result) => {
          if (!cancelled) {
            setHealth(result);
            // A successful poll clears a stale error from an earlier failed one — no page refresh needed.
            setError(null);
          }
        })
        .catch((loadError) => {
          if (!cancelled) setError(loadError instanceof ApiError ? loadError.message : "Failed to load health.");
        });
      api
        .get<{ items: MonitorItem[] }>(`/videos/${tenantId}/monitor/items`)
        .then((result) => {
          if (!cancelled) setItems(result.items);
        })
        .catch(() => {
          if (!cancelled) setItems([]);
        });
    }
    load();
    const interval = setInterval(load, 15_000); // display-only polling — never required to MAKE the backend progress
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [tenantId]);

  if (error) return <div className="error-banner">{error}</div>;
  if (health === null) return <div className="empty-state">Loading…</div>;

  return (
    <div>
      <div className="card">
        <h2>
          Overall <span className={`badge ${health.ok ? "status-finalized" : "status-failed"}`}>{health.ok ? "Healthy" : "Unhealthy"}</span>
        </h2>
        <table>
          <tbody>
            {Object.entries(health.checks).map(([name, value]) => (
              <CheckRow key={name} name={name} value={value} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Operational Counters</h3>
        {health.counters === null ? (
          <p className="muted">Counters unavailable for this deployment.</p>
        ) : (
          <div className="counter-grid">
            <div className="counter-box">
              <div className="value">{health.counters.inProgress}</div>
              <div className="label">In Progress</div>
            </div>
            <div className="counter-box">
              <div className="value">{health.counters.waitingGate2}</div>
              <div className="label">Waiting Gate 2</div>
            </div>
            <div className="counter-box">
              <div className="value">{health.counters.failedToday}</div>
              <div className="label">Failed Today</div>
            </div>
            <div className="counter-box">
              <div className="value">{health.counters.finalizedToday}</div>
              <div className="label">Finalized Today</div>
            </div>
            <div className="counter-box">
              <div className="value">{health.counters.needsManualReconciliation}</div>
              <div className="label">Needs Manual Reconciliation</div>
            </div>
            <div className="counter-box">
              <div className="value">{health.counters.paidJobsToday}</div>
              <div className="label">Paid Jobs Today</div>
            </div>
            <div className="counter-box">
              <div className="value">${health.counters.trackedProviderSpendTodayUsd.toFixed(2)}</div>
              <div className="label">Tracked Spend Today</div>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Items needing attention</h3>
        {items === null && <p className="muted">Loading…</p>}
        {items !== null && items.length === 0 && <p className="muted">Nothing needs attention right now.</p>}
        {items !== null && items.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Resource</th>
                <th>Stage</th>
                <th>Reason</th>
                <th>Last update</th>
                <th>State</th>
                <th>Safe action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={`${item.resourceType}:${item.resourceId}`}>
                  <td>
                    {item.resourceType === "video_processing_job" ? "Video job" : "Persona still"} {item.resourceId.slice(0, 8)}
                  </td>
                  <td>{item.stage}</td>
                  <td>{item.reason}</td>
                  <td>{new Date(item.lastUpdatedAt).toLocaleString()}</td>
                  <td>
                    <span className={`badge ${item.retryState === "awaiting_automatic_reconciliation" ? "status-ready_for_review" : "status-failed"}`}>{item.retryState.replaceAll("_", " ")}</span>
                  </td>
                  <td style={{ fontSize: 12, maxWidth: 320 }}>{item.safePermittedAction}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="muted">MONITOR is observational — it never triggers processing. Refreshes automatically every 15s.</p>
    </div>
  );
}
